import { SERVICE_CATEGORIES } from '@powerful/config/categories';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../icons/icon';

/**
 * As 13 categorias do PRD §5.1.1 como chips (canvas `category_chips`, G2/ADR 0045).
 *
 * `scroll`: uma linha dentro do `.h-scroller` — a página continua com 0 px de overflow. A partir de
 * 1024 px surgem setas para quem usa rato; são atalho redundante (tabindex −1, fora da árvore de
 * acessibilidade), porque o teclado já percorre os chips com Tab e o scroller acompanha o foco.
 * `wrap`: os mesmos chips a quebrar linha, dentro do painel/sheet de filtros.
 *
 * Nome canónico traduzido de `onboarding.categories.<slug>` — a mesma fonte do onboarding.
 */
export interface CategoryChipsProps {
  selected: readonly string[];
  onToggle: (categoryId: string) => void;
  variant?: 'scroll' | 'wrap';
  label: string;
}

export function CategoryChips({ selected, onToggle, variant = 'scroll', label }: CategoryChipsProps) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft <= 1, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 });
  }, []);

  useEffect(() => {
    if (variant !== 'scroll') return;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, variant]);

  const chips = SERVICE_CATEGORIES.map((c) => {
    const on = selected.includes(c.id);
    return (
      <button key={c.id} className="hit" type="button" aria-pressed={on} onClick={() => onToggle(c.id)} data-category={c.slug}>
        <span className={on ? 'chip-cat on' : 'chip-cat'}>{t(`onboarding.categories.${c.slug}`)}</span>
      </button>
    );
  });

  if (variant === 'wrap') {
    return (
      <div className="pc-wrap" role="group" aria-label={label}>
        {chips}
      </div>
    );
  }

  const page = (dir: 1 | -1) => {
    const el = scroller.current;
    el?.scrollBy({ left: dir * el.clientWidth * 0.8 });
  };

  return (
    <div className="pc-chiprow">
      <button
        className="hit btn-icon pc-chipnav"
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        style={{ visibility: edges.start ? 'hidden' : 'visible' }}
        onClick={() => page(-1)}
        data-testid="chips-prev"
      >
        <Icon name="chevLeft" size={18} />
      </button>
      <div ref={scroller} className="h-scroller" role="group" aria-label={label} onScroll={measure}>
        {chips}
      </div>
      <button
        className="hit btn-icon pc-chipnav"
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        style={{ visibility: edges.end ? 'hidden' : 'visible' }}
        onClick={() => page(1)}
        data-testid="chips-next"
      >
        <Icon name="chevRight" size={18} />
      </button>
    </div>
  );
}
